import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { notification } from 'tea-component';

import type {
  IYamlBatchOperationResultItem,
  IYamlConfigSaveItem,
  IYamlConfigRetrieveDetail,
} from '@/pages/datalab/pages/Number/ModelManagementCenter/types';
import { normalizeDatalabRequestError } from '@/pages/datalab/pages/Number/ModelManagementCenter/utils/normalizeDatalabRequestError';

import { WizardStep } from '../constant';
import { generateYamlFromConfig, getFieldConfig, saveYamlConfigs } from '../service';
import type {
  IGenerateYamlFromConfigRawResponse,
  IStepFieldConfigState,
  IStepSelectTablesState,
  IStepYamlDraftState,
  IUseModelAgentCreateWizardParams,
  IUseModelAgentCreateWizardResult,
  IYamlWorkflow,
  TGenerateYamlDraftOptions,
  TStepFieldConfigPatch,
  TStepSelectTablesPatch,
  TStepYamlDraftPatch,
  TYamlWorkflowPatch,
} from '../types';
import { buildApplyYamlRetrievePatches } from '../utils/applyYamlRetrieveDetail';
import { buildCubeConfig } from '../utils/buildCubeConfig';
import { buildSaveYamlItem } from '../utils/buildSaveYamlItem';
import { getFieldConfigPatch } from '../utils/mapFieldConfigFromApi';
import { countFieldKinds } from '../utils/fieldConfigRowUtils';
import {
  createAgentCreateCacheManager,
  type IAgentCreateWizardSnapshot,
} from '../utils/agentCreateCacheManager';
import {
  buildFieldConfigLoadKey,
  cloneYamlWorkflowState,
  isFieldConfigCacheHit,
  isTableSelectionChanged,
  setFieldConfigLoading,
  yamlDraftResetForNewTable,
} from '../utils/wizardHelpers';
import {
  createInitialFieldConfigState,
  createInitialSelectTablesState,
  createInitialYamlDraftState,
  fromWizardPersistSnapshot,
  toWizardPersistSnapshot,
} from '../utils/wizardStateInitial';
import { useWizardYamlFeedback } from './useWizardYamlFeedback';

/**
 * Agent Cube 创建向导核心逻辑（与页面解耦）：
 * Step1 拉字段 → Step2 配维度/指标 → Step3 生成 YAML → Step4 保存草稿入口状态。
 * 权限校验、埋点、Stepper 文案由 `index.tsx` 维护。
 */
export function useModelAgentCreateWizard(
  params: IUseModelAgentCreateWizardParams,
): IUseModelAgentCreateWizardResult {
  const { gameCode, editYamlDetail } = params;
  const { formatMessage } = useIntl();

  /** 当前向导步（与 Stepper / 条件渲染一一对应） */
  const [current, setCurrent] = useState<WizardStep>(WizardStep.One);
  /** Step1：库 / schema / 多表选择（子组件只提交 patch） */
  const [selectTables, setSelectTables] = useState<IStepSelectTablesState>(
    createInitialSelectTablesState,
  );
  /** Step2：列元数据 + 维度/指标行内编辑草稿 */
  const [fieldConfig, setFieldConfig] = useState<IStepFieldConfigState>(
    createInitialFieldConfigState,
  );
  /** Step3：YAML 文本、生成 loading、save 返回的 uuid（供 Step4 测试/发布） */
  const [yamlDraft, setYamlDraft] = useState<IStepYamlDraftState>(
    createInitialYamlDraftState,
  );
  /**
   * Step4：测试/发布区 UI 状态（与 `INITIAL_YAML_WORKFLOW` 解耦拷贝，
   * 避免与常量共享引用被就地改写）。
   */
  const [yamlWorkflow, setYamlWorkflow] = useState<IYamlWorkflow>(
    cloneYamlWorkflowState,
  );

  /**
   * 上次成功拉取 Step2 字段时的「game + 库表选择」快照；
   * 与当前选择一致时可跳过 getFieldConfig。
   */
  const fieldConfigLoadKeyRef = useRef<string>('');

  /** 各 Step 仅提交 Partial，由父级合并，避免子组件扩散全量 setState。 */
  const changeSelectTables = useCallback((patch: TStepSelectTablesPatch) => {
    setSelectTables((prev) => ({ ...prev, ...patch }));
  }, []);

  const changeFieldConfig = useCallback((patch: TStepFieldConfigPatch) => {
    setFieldConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const changeYamlDraft = useCallback((patch: TStepYamlDraftPatch) => {
    setYamlDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const changeYamlWorkflow = useCallback((patch: TYamlWorkflowPatch) => {
    setYamlWorkflow((prev) => ({ ...prev, ...patch }));
  }, []);

  /** 恢复 Step4 测试条初始态；rows 深拷贝一层，避免污染 `constant` 中的模板对象。 */
  const resetYamlWorkflow = useCallback(() => {
    setYamlWorkflow(cloneYamlWorkflowState());
  }, []);

  /**
   * 编辑态 `getYaml` 成功后写入向导：YAML 草稿、Step2 fieldConfig、
   * Step1 库表及内部字段缓存 key。页面在 `setEditYamlDetail` 之后调用即可。
   */
  const applyYamlRetrieveDetail = useCallback(
    (detail: IYamlConfigRetrieveDetail, routeUuid: string) => {
      const patches = buildApplyYamlRetrievePatches(
        detail,
        routeUuid,
        gameCode,
      );
      changeYamlDraft(patches.yamlDraft);
      changeFieldConfig(patches.fieldConfig);
      if (patches.selectTables) {
        changeSelectTables(patches.selectTables);
      }
      if (patches.fieldConfigLoadKey) {
        fieldConfigLoadKeyRef.current = patches.fieldConfigLoadKey;
      }
    },
    [changeFieldConfig, changeSelectTables, changeYamlDraft, gameCode],
  );

  /** 整包导出当前向导 state，与 restore 字段一一对应（供 `useModelAgentCreateCache`） */
  const getWizardSnapshot = useCallback((): IAgentCreateWizardSnapshot => {
    return toWizardPersistSnapshot({
      current,
      selectTables,
      fieldConfig,
      yamlDraft,
      fieldConfigLoadKey: fieldConfigLoadKeyRef.current,
    });
  }, [current, fieldConfig, selectTables, yamlDraft]);

  /** 整包写回 state，不与 prev 合并，避免残留未快照字段 */
  const restoreWizardSnapshot = useCallback(
    (snapshot: IAgentCreateWizardSnapshot): void => {
      const restored = fromWizardPersistSnapshot(snapshot);
      setCurrent(restored.current);
      setSelectTables(restored.selectTables);
      setFieldConfig(restored.fieldConfig);
      setYamlDraft(restored.yamlDraft);
      fieldConfigLoadKeyRef.current = restored.fieldConfigLoadKey;
    },
    [],
  );

  // 从 Step3 再进 Step4 时需清掉上一轮测试/loading，否则 UI 残留「已通过」等状态。
  useEffect(() => {
    if (current === WizardStep.Four) {
      resetYamlWorkflow();
    }
  }, [current, resetYamlWorkflow]);

  /** Step3/4 底部摘要；cubeNameSummary 仅取自 yamlDraft.cube_name */
  const cardSummary = useMemo(() => {
    const { dimCount, measCount } = countFieldKinds(fieldConfig);
    return {
      tablesSummary: selectTables.tablesName || '-',
      dimCount,
      measCount,
      cubeNameSummary: yamlDraft.cube_name?.trim() || '-',
    };
  }, [fieldConfig.fieldColumns, selectTables.tablesName, yamlDraft.cube_name]);

  const { showSaveDraftFailure, showGenerateYamlFailure } = useWizardYamlFeedback(cardSummary.cubeNameSummary);

  /**
   * Step1「下一步」：get_field_config。
   * 选表未变且已有列数据时短路；改表时重置 cube_name/YAML（勿沿用编辑 retrieve 名）。
   */
  const handleStepOneNext = useCallback((): void => {
    if (fieldConfig.fieldLoading) {
      return;
    }
    const primaryTableId = selectTables.tablesValue.filter(Boolean)[0];
    if (!primaryTableId || !gameCode) {
      notification.error({
        description: formatMessage({
          id: 'dbl_model_agent_field_config_need_tables',
        }),
      });
      return;
    }
    const loadKey = buildFieldConfigLoadKey(gameCode, selectTables);
    const prevKey = fieldConfigLoadKeyRef.current;
    // 编辑态回退 Step1 改库/表：generate 须用新表名，勿沿用 retrieve 的 cube_name / YAML
    if (isTableSelectionChanged(loadKey, prevKey)) {
      changeYamlDraft(yamlDraftResetForNewTable(selectTables.tablesName || ''));
      resetYamlWorkflow();
    }
    // 选择未变且已有列数据：避免重复请求（用户 Step1↔Step2 往返）
    if (isFieldConfigCacheHit(loadKey, prevKey, fieldConfig)) {
      setCurrent(WizardStep.Two);
      return;
    }
    setFieldConfig((prev) => ({ ...prev, ...setFieldConfigLoading(true) }));
    getFieldConfig({
      game_code: gameCode,
      primary_table_id: primaryTableId,
      table_ids: [primaryTableId],
    })
      // http 层已剥壳，此处仍按 unknown 收窄，兼容后端字段微调
      .then((data: unknown) => {
        const patch = getFieldConfigPatch(
          (data || {}) as { tables?: unknown[]; },
        );
        if (!patch?.fieldColumns?.length) {
          setFieldConfig((prev) => ({
            ...prev,
            ...setFieldConfigLoading(false, true),
          }));
          notification.error({
            description: formatMessage({
              id: 'dbl_model_agent_field_config_empty_catalog',
            }),
          });
          return false;
        }
        fieldConfigLoadKeyRef.current = loadKey;
        setFieldConfig((prev) => ({ ...prev, ...patch }));
        return true;
      })
      .catch(() => {
        setFieldConfig((prev) => ({
          ...prev,
          ...setFieldConfigLoading(false, true),
        }));
        notification.error({
          description: formatMessage({
            id: 'dbl_model_agent_field_config_fetch_failed',
          }),
        });
        return false;
      })
      // 仅在拉取成功且列非空时切步；失败分支已在上方 return false
      .then((ok) => {
        if (ok) {
          setCurrent(WizardStep.Two);
        }
      });
  }, [
    changeYamlDraft,
    fieldConfig,
    formatMessage,
    gameCode,
    resetYamlWorkflow,
    selectTables,
  ]);

  /**
   * 调 generate_yaml_from_config；`advanceOnSuccess` 为 true 时成功后进 Step3。
   * 编辑态或已 save 草稿时须带 uuid；成功后会重置 Step4 工作流（新 YAML 与旧测试结果无关）。
   */
  const handleGenerateYaml = useCallback(
    (options?: TGenerateYamlDraftOptions): void => {
      if (yamlDraft.yamlLoading) {
        return;
      }
      // 编辑 retrieve / 已存草稿：再生成须传 uuid，且不在此清空 savedConfigUuid
      const configUuid = yamlDraft.savedConfigUuid?.trim() || editYamlDetail?.uuid?.trim();
      setYamlDraft((prev) => ({
        ...prev,
        yamlLoading: true,
        yamlError: false,
      }));

      generateYamlFromConfig({
        game_code: gameCode,
        schema: selectTables.schemaValue,
        uuid: configUuid,
        cube_config: buildCubeConfig({
          selectTables,
          fieldConfig,
          cubeName: yamlDraft.cube_name,
        }),
      })
        .then((res: IGenerateYamlFromConfigRawResponse) => {
          const firstYaml = res?.data?.data?.data_list?.[0];
          const yamlText =
            typeof firstYaml?.yaml_context === 'string'
              ? firstYaml.yaml_context.trim()
              : '';
          // rawData 整包判断；yaml_context 空串视为失败，避免 Step3「就绪」却无内容
          if (res?.code !== 0 || !yamlText) {
            setYamlDraft((prev) => ({
              ...prev,
              yamlLoading: false,
              yamlError: true,
            }));
            const { msg: resMsg } = normalizeDatalabRequestError(res);
            showGenerateYamlFailure({
              kind: 'single',
              msg:
                (resMsg && String(resMsg).trim()) ||
                formatMessage({
                  id: 'dbl_model_management_create_model_generate_failed_hint',
                }),
            });
            return;
          }

          setYamlDraft((prev) => ({
            ...prev,
            cube_name: firstYaml?.cube_name || prev.cube_name,
            yamlReady: true,
            yamlText,
            yamlLoading: false,
            yamlError: false,
          }));
          notification.success({
            description: formatMessage(
              { id: 'dbl_model_management_create_model_generate_success' },
              { name: firstYaml?.cube_name },
            ),
          });
          // 新 YAML 与旧测试结果无关，重置 Step4 行状态
          resetYamlWorkflow();
          // 如果 options.advanceOnSuccess 为 true，则进入 Step3
          if (options?.advanceOnSuccess) {
            setCurrent(WizardStep.Three);
          }
        })
        .catch((err: unknown) => {
          setYamlDraft((prev) => ({
            ...prev,
            yamlLoading: false,
            yamlError: true,
          }));
          showGenerateYamlFailure({ kind: 'request', err });
        });
    },
    [
      editYamlDetail,
      fieldConfig,
      formatMessage,
      gameCode,
      resetYamlWorkflow,
      selectTables,
      showGenerateYamlFailure,
      yamlDraft.cube_name,
      yamlDraft.savedConfigUuid,
      yamlDraft.yamlLoading,
    ],
  );

  const handleStepTwoNext = useCallback((): void => {
    handleGenerateYaml({ advanceOnSuccess: true });
  }, [handleGenerateYaml]);

  /** Step3「下一步」：save 草稿，成功写入 uuid 后进入 Step4。 */
  const handleStepThreeNext = useCallback((): void => {
    const yamlText = yamlDraft.yamlText.trim();
    if (!yamlText || yamlDraft.yamlLoading || yamlWorkflow.saveDraftLoading) {
      return;
    }

    changeYamlWorkflow({ saveDraftLoading: true });
    const baseItem = buildSaveYamlItem({
      gameCode,
      yamlText: yamlDraft.yamlText,
      cubeName: yamlDraft.cube_name,
      selectTables,
      fieldConfig,
      savedUuid: yamlDraft.savedConfigUuid,
    });
    // 编辑态：retrieve 全量作底（uuid、权限、创建时间等），再用当前向导组装的 baseItem
    // 覆盖库表 / yaml / cube_config_json 等；避免用户回退改表后仍提交旧 retrieve 数据
    const hasEditYamlBase =
      editYamlDetail !== null && Object.keys(editYamlDetail).length > 0;
    const item: IYamlConfigSaveItem =
      hasEditYamlBase && editYamlDetail
        ? { ...editYamlDetail, ...baseItem }
        : baseItem;

    saveYamlConfigs({ items: [item] })
      .then((saveRes) => {
        // 与模型中心批量接口一致：取 results[0].uuid / code
        const first = (saveRes as { results?: IYamlBatchOperationResultItem[]; })
          .results?.[0];
        if (!first || first.code !== 0) {
          showSaveDraftFailure({
            kind: 'single',
            msg:
              (first?.msg && String(first.msg).trim()) ||
              formatMessage({
                id: 'dbl_model_management_create_model_save_failed_hint',
              }),
          });
          return;
        }
        changeYamlDraft({
          cube_name: first?.cube_name || first?.name || '',
          savedConfigUuid: String(first.uuid ?? ''),
        });
        // 草稿已入库，本地未保存进度缓存不再需要
        createAgentCreateCacheManager().clear();
        notification.success({
          description: formatMessage(
            { id: 'dbl_model_management_create_model_save_success' },
            { name: cardSummary.cubeNameSummary },
          ),
        });
        setCurrent(WizardStep.Four);
      })
      .catch((err: unknown) => {
        showSaveDraftFailure({ kind: 'request', err });
      })
      .finally(() => {
        changeYamlWorkflow({ saveDraftLoading: false });
      });
  }, [
    cardSummary.cubeNameSummary,
    changeYamlDraft,
    changeYamlWorkflow,
    editYamlDetail,
    fieldConfig,
    formatMessage,
    gameCode,
    selectTables,
    showSaveDraftFailure,
    yamlDraft,
    yamlWorkflow.saveDraftLoading,
  ]);

  // 供页面与各 Step 拆分的 props；不在此 hook 内做权限与 Stepper
  return {
    current,
    setCurrent,
    selectTables,
    changeSelectTables,
    fieldConfig,
    changeFieldConfig,
    yamlDraft,
    changeYamlDraft,
    yamlWorkflow,
    changeYamlWorkflow,
    cardSummary,
    handleStepOneNext,
    handleStepTwoNext,
    handleStepThreeNext,
    handleGenerateYaml,
    applyYamlRetrieveDetail,
    getWizardSnapshot,
    restoreWizardSnapshot,
  };
}
